import React, { useCallback, useContext } from "react";

import { emitAddToCart, getSourcePlatform } from "@catchoftheday/analytics";
import { Button, ButtonProps } from "@catchoftheday/cg-components";
import { CustomerContext } from "@catchoftheday/customer";

import {
  AddItemsToCartMutationVariables,
  Maybe,
  SourcePlatformType,
} from "../../generated/graphql";
import { MutationType, useToast } from "../../hooks";

type AddItemsToCartButtonProps = {
  items: AddItemsToCartItem[];
  onClick?: () => void;
  setError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

type AddItemsToCartItem = {
  offerId: string;
  quantity: number;
  imageUrl: string;
  title: string;
  sourceId?: string;
  sourceType?: number;
  sourcePosition?: number;
  eventId?: number;
};

export const AddItemsToCartButton: React.FC<
  AddItemsToCartButtonProps & ButtonProps
> = ({ items, onClick, setError, setLoading, children, ...buttonProps }) => {
  const { data: customer } = useContext(CustomerContext);

  const addItemsToCart = useToast<AddItemsToCartMutationVariables>({
    mutationType: MutationType.ADD_ITEMS_TO_CART,
    setError,
    setLoading,
  });

  const toastValues = items.map(({ imageUrl, title, offerId }) => ({
    imageUrl,
    title,
    offerId,
  }));
  const itemsToCartValues = items.map(({ offerId, quantity }) => ({
    offerId,
    quantity,
    sourcePlatform: getSourcePlatform() as
      | Maybe<SourcePlatformType>
      | undefined,
  }));

  const onAddToCart = useCallback(async () => {
    const { status } = await addItemsToCart(toastValues, {
      itemsToBeAdded: { items: itemsToCartValues },
    });
    if (status !== "ServerError") {
      items.forEach((item) => {
        emitAddToCart({
          customer_id: customer?.id,
          success:
            Array.isArray(status) &&
            status.find((error) => error.offerId == item.offerId)
              ? false
              : true,
          source_id: item.sourceId,
          source_type: item.sourceType,
          source_position: item.sourcePosition,
          offer_id: item.offerId,
          event_id: item.eventId || 1,
        });
      });
    }
    if (typeof onClick === "function") {
      onClick();
    }
  }, [addItemsToCart, toastValues, itemsToCartValues, items, customer?.id]);

  return (
    <Button onClick={onAddToCart} aria-label="Add to cart" {...buttonProps}>
      {children}
    </Button>
  );
};
